<template>
  <div class="w-full" data-testid="videos-section">
    <div
      v-if="loading"
      class="flex h-[444px] w-full items-center justify-center"
    >
      <TheLoader />
    </div>
    <div v-if="!loading && featuredVideo" class="w-full">
      <SectionTitle title="Videos" :url="viewMoreUrl" />

      <div v-if="!loading" class="mb-2.5 overflow-x-hidden">
        <div v-if="featuredVideo" class="relative flex gap-x-2">
          <HeroVideoCard
            v-if="featuredVideo"
            class="hidden min-h-[319px] xl:flex"
            :index="0"
            :slug="featuredVideo.slug"
            :title="featuredVideo.title"
            :excerpt="featuredVideo.excerpt"
            :duration="featuredVideo.duration"
            :published-date="featuredVideo.publishedDate"
            :cover-url="featuredVideo.backgroundImage"
            :is-authorized="!!featuredVideo.vimeoId"
            :preview-vimeo-id="featuredVideo.previewVimeoId"
            :show-progress-bar="videos.length > 1"
            @play="handleVideoClick(featuredVideo)"
          />
          <Swiper
            v-if="videos.length > 1"
            class="video-card-swiper"
            data-testid="swiper-videos"
            :modules="modules"
            :width="901"
            :slides-per-view="3"
            :space-between="8"
            :speed="500"
            :autoplay="{
              delay: 9500,
              disableOnInteraction: false,
            }"
            loop
            @slide-change="slideChange"
            @swiper="onSwiper"
          >
            <SwiperSlide
              v-for="(video, i) in videos"
              :key="video.id + i"
              :data-testid="`video-swiper-slide-${i}`"
              class="pb-4"
            >
              <VideoCard
                v-if="video"
                :index="i"
                :slug="video.slug"
                :title="video.title"
                :excerpt="video.excerpt"
                :duration="video.duration"
                :published-date="video.publishedDate"
                :cover-url="video.backgroundImage"
                :is-active-index="activeIndex === i"
                :vimeo-id="video.vimeoId"
                :preview-vimeo-id="video.previewVimeoId"
                @play="handleVideoClick(video)"
              />
            </SwiperSlide>
          </Swiper>
        </div>
      </div>

      <div v-if="videos.length > 1" class="flex gap-7 text-p-500">
        <button
          class="rounded-full border-2 border-white bg-white p-1 shadow-md transition-all hover:border-s-400"
          data-testid="swiper-prev-button"
          @click="next"
        >
          <IconChevronLeft class="h-4 w-4" />
        </button>
        <button
          class="rounded-full border-2 border-white bg-white p-1 shadow-md transition-all hover:border-s-400"
          data-testid="swiper-next-button"
          @click="previous"
        >
          <IconChevronLeft class="h-4 w-4 rotate-180" />
        </button>
      </div>
    </div>
    <VideoModal
      v-if="vimeoVideo && $modal.getModal('videoIntro')"
      :vimeo-source="vimeoSource"
      :video-ratio="videoRatio"
      :is-preview="isPreview"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay } from 'swiper/modules'
import HeroVideoCard from '~/components/portals/HeroVideoCard.vue'
import VideoCard from '~/components/portals/VideoCard.vue'
import VideoModal from '~/components/video/VideoModal.vue'
import SectionTitle from '~/components/portals/SectionTitle.vue'
import IconChevronLeft from '~/assets/icons/chevron-left.svg'
import { getServices } from '~/constants/services'
import { useNuxtApp } from '#app'
import { useGetVimeoData } from '~/composables/useGetVimeoData'

import 'swiper/css'
import 'swiper/css/autoplay'

const props = defineProps<{
  productSlug: string
  videos: any[]
}>()

const { $modal, $rudderstack } = useNuxtApp()
const { vimeoVideo, vimeoSource, videoRatio, getVimeoData, handlerPlay } =
  useGetVimeoData()

const swiper = ref(null)
const modules = [Autoplay]
const activeIndex = ref(0)
const loading = ref(true)
const isPreview = ref(false)

const featuredVideo = computed(() => {
  if (props.videos.length === 0) return null
  if (activeIndex.value === 0) return props.videos[props.videos.length - 1]
  return props.videos[activeIndex.value - 1]
})

const viewMoreUrl = computed(() => {
  return `/${props.productSlug}/market-insights/videos`
})

onMounted(async () => {
  loading.value = false
})

const onSwiper = (swiperInstance) => {
  swiper.value = swiperInstance
}

const slideChange = (swiperInstance) => {
  activeIndex.value = swiperInstance.realIndex
}

const handleVideoClick = async (vimeoData) => {
  const vimeoId = vimeoData.vimeoId || vimeoData.previewVimeoId
  isPreview.value = !vimeoData.vimeoId && !!vimeoData.previewVimeoId
  const service = [props.productSlug, 'source']

  const initialValues = getServices([...service])
  $modal.setInitialFields(initialValues)

  if (!vimeoId) {
    $rudderstack.trackContentGatedInteraction()
    return $modal.open('contact')
  }
  await getVimeoData(vimeoId)
  handlerPlay()
}

const next = () => {
  swiper.value?.slidePrev()
}

const previous = () => {
  swiper.value?.slideNext()
}
</script>
